// Direct imports
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { Scene } from "@babylonjs/core/scene";
import { Matrix, Vector3, Quaternion } from "@babylonjs/core/Maths/math.vector";
import { InspectableType } from "@babylonjs/core/Misc/iInspectable";
import { SceneObjectData } from "../scenedata/sceneobjectdata";
import type { ForeignNodeData } from "../loaders/formats/gltf21/foreign";
import type { AnimationBindingData } from "../scenedata/sceneobjectdata";
import type { TransformData } from "../scenedata/transformdata";
import type { TransitionConfig } from "../scenedata/behaviors/propertiesdata";
import { isTimeTransition } from "../scenedata/behaviors/propertiesdata";
import { getTransitionManager } from "./TransitionManager";
import type { Node } from "@babylonjs/core/node";
import { Observable } from "@babylonjs/core/Misc/observable";
import type { Observer } from "@babylonjs/core/Misc/observable";
import { TraceBehavior, WHOLE_REF_RE, wholeRefName } from "../behaviors/tracebehavior";
import type { PropertyProvider, PropertyValue } from "../behaviors/tracebehavior";

// Type-only imports for typed getBehavior() string overloads — no runtime cycle.
import type { Animation } from "../behaviors/animation";
import type { Properties } from "../behaviors/properties";
import type { Layout } from "../behaviors/layout";
import type { Frame } from "../behaviors/frame";
import type { BehaviorConstructor, DataOf } from "../behaviors/BehaviorRegistry";
import { getBehaviorConstructor } from "../behaviors/BehaviorRegistry";
import { BehaviorData } from "../scenedata/behaviordata";
import { createDefaultData } from "../scenedata/schemas/defaults";
import { ResourceManager } from "../loaders/ResourceManager";
import { ConfigData } from "./configdata";
import { Logger } from '../utils/Logger';
import { Utility } from '../utils/Utility';
import { getAnimationDriver } from '../utils/drivenAnimation';
import { isSceneFormatRef } from '../loaders/plugins/sceneFormatExtensions';
import { matchAssetFileUrlPath, type ContentRef } from '../content/assetRef';
import type { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";
import { getObjectMeshes } from '../utils/MeshUtils';
import { restoreChildSlot } from '../utils/restoreChildSlot';
import type { AnimationGroup } from "@babylonjs/core/Animations/animationGroup";
import type { TraceScene, SceneEventSystem } from './TraceScene';

/**
 * Per-component transform-transition channel id. Each maps to KHR's
 * corresponding transform pointer (translation / rotation / scale).
 */
type TransformChannel = 'pos' | 'rot' | 'scale';

/**
 * Returns true if the behavior's constructor declares `cmsManaged = true`.
 * Such behaviors hold data owned by an external CMS, not authored in the
 * `.trace` file — they are filtered out by exporters but still round-trip
 * through the publish-metadata channel.
 *
 * Exported (internal, not part of the package API) so the document writer
 * (`TraceScene._clearAnchorHostPose`) applies the same test as
 * {@link SceneObject.serializeBehaviors}' `excludeCmsManaged`.
 */
export function isCmsManagedBehavior(behavior: TraceBehavior): boolean {
    const ctor = behavior.constructor as { cmsManaged?: boolean };
    return ctor.cmsManaged === true;
}

/**
 * Late-bound reference to SceneCreation.applyComponents, set by SceneCreation
 * at import time. Breaks the SceneObject ↔ SceneCreation import cycle.
 */
let _applyComponents: ((sceneObject: SceneObject, sceneName: string) => Promise<void>) | null = null;

/** @internal Called by SceneCreation to register itself. */
export function setApplyComponents(fn: (sceneObject: SceneObject, sceneName: string) => Promise<void>): void {
    _applyComponents = fn;
}

// 2024
// Author: Aaron Post

// Apply transform data to a transform node.
export function applyTransform(
    transformData: TransformData,
    transformNode: TransformNode
): void {
    if (transformData.translation) {
        transformNode.position = new Vector3(
            transformData.translation[0],
            transformData.translation[1],
            transformData.translation[2]
        );
    }

    if (transformData.rotation) {
        transformNode.rotationQuaternion = new Quaternion(
            transformData.rotation[0],
            transformData.rotation[1],
            transformData.rotation[2],
            transformData.rotation[3]
        );
    }

    if (transformData.scale) {
        transformNode.scaling = new Vector3(
            transformData.scale[0],
            transformData.scale[1],
            transformData.scale[2]
        );
    }
}

export class SceneObject {
    transform!: TransformNode;
    name: string;
    private _sceneObjectData: SceneObjectData;
    babylonScene: Scene;
    private behaviors: TraceBehavior[];
    private _src: string;
    /** glTF extensions used by this object's loaded asset */
    public extensionsUsed?: string[];
    /** Runtime-only: when true, Model resets GLB root transform on load. Set by plugin loaders. NOT serialized. */
    public _resetAssetTransform = false;

    /**
     * Runtime-only: the loader already instantiated this object's `src` into
     * the scene, so the "src as template" rule must not add an asset-owning
     * behavior that would load the same file again. Set by plugin loaders
     * (see `LoadedSceneNode.assetInstantiated`). NOT serialized — it is a
     * fact about THIS load, not about the content.
     */
    public _assetInstantiated = false;

    /**
     * Runtime-only: the folder-relative file this object's own node named
     * BEFORE its `src` was replaced by the reference URL it stands in for.
     *
     * A §3.1 leaf wrapper is one node whose `externalAsset` is
     * `assets/<hash>.glb` — relative to the ASSET's folder. The placed object
     * is the stand-in for the whole reference, so its authored `src` is the
     * asset file URL and the relative path stays where it belongs, inside the
     * asset's folder (#807). This is that path, kept because "Copy to scene"
     * (spec §2.6) has to know which bytes to bring across when it dissolves
     * the reference — `TraceScene.unpackReference`.
     *
     * NOT serialized: a scene may not name a file another folder owns, which
     * is exactly why unpacking one has to copy it.
     */
    public _standInFileSrc: string | undefined;

    /** Fires when a behavior builds, rebuilds or recolors a mesh this object owns. */
    public readonly onOwnedMeshesChangedObservable = new Observable<void>();

    /**
     * Fires after {@link addBehavior} has loaded and attached a built-in
     * behavior on a live object (a script behavior does not fire it). Scene
     * loads do not fire it: they register every behavior before any attaches,
     * so a sibling lookup in `onAttach` already sees the whole set. This is
     * for a behavior that needs a sibling added later.
     */
    public readonly onBehaviorAddedObservable = new Observable<TraceBehavior>();

    /**
     * Runtime-only animation transform node.
     * Sits between `this.transform` and visual children when any animation
     * behavior calls `getOrCreateAnimationRoot()`. NOT serialized.
     * Scene graph traversal skips this node transparently.
     */
    private _animationRoot: TransformNode | null = null;

    /** Second runtime node between transform and animation root — sequence
     *  transitions (enter/exit) animate it while the loop animates the root.
     *  Lazily created via getOrCreateAnimationLayer(). NOT serialized. */
    private _animationLayer: TransformNode | null = null;

    /** Cached effective active state (own enabled ∧ ancestors). See
     *  {@link effectiveActive}. */
    private _effectiveActive = true;

    /** Subscription to the transform's enabled-state observable — the single
     *  choke point every setEnabled path funnels through. */
    private _enabledStateObserver: Observer<boolean> | null = null;

    /** Animation clips keyed by clean clip name */
    private _animationClips: ReadonlyMap<string, AnimationGroup> = new Map();

    /**
     * Clips bound to this object's instance by `TRACE_animation_binding`
     * (§2.4), keyed by the source animation's name. Owned here: replaced on
     * every re-bind and disposed with the object.
     */
    private _boundAnimationClips: ReadonlyMap<string, AnimationGroup> = new Map();
    /** Driven animations of the model (`extras.trace.driver`) — see {@link drivenAnimationClips}. */
    private _drivenAnimationClips: readonly AnimationGroup[] = [];

    /** {@link animationClips}, merged once per change. */
    private _mergedAnimationClips: ReadonlyMap<string, AnimationGroup> | null = null;

    // Dependencies for reload - injected at construction
    private _resources: ResourceManager;
    private _config: ConfigData;
    private _eventTarget: SceneEventSystem | null;

    /** Reference to the TraceScene for cross-object resolution */
    private _traceScene: TraceScene;

    // ── Property reference support ($name in active/src/transform) ──

    /** Property names referenced in authored SceneObjectData (active, src, transform elements) */
    private _propertyNames: Set<string> = new Set();

    /** Tracks which PropertyProvider each name is subscribed to, for cleanup */
    private _subscribedProperties: Map<string, PropertyProvider> = new Map();

    /** Resolved active state — may differ from _sceneObjectData.active when it's a $ref */
    private _resolvedActive: boolean = true;

    /** Runtime visibility a state lays over the authored flag; never serialized. */
    private _activeOverride: boolean | undefined;

    /** Runtime visibility a Timeline cue lays over a state's; never serialized. */
    private _cueActive: boolean | undefined;

    // ── Transform transition state ──

    /**
     * Transform-transition channels currently active in TransitionManager.
     * One channel per component ('pos' | 'rot' | 'scale') — KHR
     * pointer/interpolate semantics: each transform pointer animates
     * independently, and a new write to one pointer never disturbs another's
     * in-flight interpolation.
     */
    private _activeTransformChannels = new Set<TransformChannel>();

    /**
     * Set when the loader classified `src` as a reference to another scene
     * document from the manifest rather than from its extension.
     * See {@link isSceneReference}.
     */
    private _sceneRef = false;

    /** Cache for {@link contentRef}, keyed by the `src` it was derived from.
     *  `_contentRefSrc` starts as a value `_src` can never hold (it is always a
     *  string) so the first read always computes. */
    private _contentRefSrc: string | null = null;
    private _contentRefName: string | undefined = undefined;
    private _contentRef: ContentRef | null = null;

    /**
     * `files[k].name` — the ORIGINAL human name of the file `src` points at
     * (spec §2.1), when the document carried one. Informational: storage names
     * files by hash, nothing resolves by this, and it is simply absent for a
     * document that never recorded it.
     */
    private _srcName: string | undefined = undefined;

    /** The asset source path (e.g. "./assets/model.glb", "builtin:primitive_cube.glb"). */
    get src(): string {
        return this._src;
    }

    /**
     * The original human name of the file `src` points at (`chair.glb`), when
     * the document recorded one in `files[k].name` (spec §2.1, §3.6).
     *
     * Editor information only — a label for a node whose `src` is a storage
     * name (`assets/my-chair-v2-1.8b10a3f77d4e21c0.glb`, spec §2.1) or a
     * library-reference URL. The slug half is lossy and this is not, which is
     * why the field exists; nothing is ever resolved by it.
     */
    get srcName(): string | undefined {
        return this._srcName;
    }

    /**
     * @internal
     * Record the file's original human name, from `files[k].name`. Set by the
     * loader; informational (spec §2.1).
     */
    public _setSrcNameInternal(name: string | undefined): void {
        this._srcName = name;
    }

    /**
     * Whether `src` references another scene rather than an asset.
     *
     * True for a scene-format archive (`.trace`/`.tracex`/`.zip`/`.real`/
     * `.glxf`, recognized by extension) and for a **library reference** — an
     * absolute reference the document's manifest declared `model/gltf+json`,
     * whose extension says nothing (spec §3.4). Reference subtrees are derived
     * content: they load through the nested-scene path, refuse authored
     * children (`canParentUnder`), and their descendants are dropped on export.
     */
    get isSceneReference(): boolean {
        return this._sceneRef || isSceneFormatRef(this._src);
    }

    /**
     * @internal
     * Mark this object as a scene reference. Set by the loader when the
     * document's manifest says so; the extension test cannot see it.
     */
    public _setSceneRefInternal(sceneRef: boolean): void {
        this._sceneRef = sceneRef;
    }

    /**
     * The library asset this object references, when `src` is an asset file
     * URL — **editor information only** (spec §3.4).
     *
     * A library reference's `src` is `https://<api>/assets/<guid>/file`, and
     * the id in that path is the only handle an editor needs to offer "open
     * asset", show the library thumbnail, or use the library's name. Rendering
     * reads nothing from it: the URL 302s to a document and the loader follows
     * it like any other reference, which is the whole point of putting the id
     * in the path rather than inventing a second carrier.
     *
     * Nothing here fetches. `assetId` is the id and that is all — the row is
     * the host's to load, if the host wants it.
     *
     * Derived, never stored: the match runs against whatever `src` currently
     * resolves to, so a `$ref` src that re-resolves, an unpack that clears
     * `src`, or an inspector edit all move this with it. Null for a
     * folder-relative file (`assets/<hash>.glb`), a `blob:` src, a builtin, and
     * for any URL on our host that is not the exact `/assets/{guid}/file` form.
     */
    get contentRef(): ContentRef | null {
        // Memoized on the src it was derived from: editors read this per frame
        // in list and inspector views, and URL parsing is not free.
        if (this._contentRefSrc !== this._src || this._contentRefName !== this._srcName) {
            this._contentRefSrc = this._src;
            this._contentRefName = this._srcName;
            const assetId = matchAssetFileUrlPath(this._src);
            this._contentRef = assetId
                ? { assetId, ...(this._srcName ? { name: this._srcName } : {}) }
                : null;
        }
        return this._contentRef;
    }

    /**
     * @internal
     * Stamp a src URL onto an already-constructed SceneObject. Used by the
     * scene-format import path so the wrapper of a URL-loaded `.trace` carries
     * the original URL through to export (where it's emitted as a reference).
     * Skipped through the loader's nested-asset auto-fetch because it runs
     * post-construction.
     */
    public _setSrcInternal(src: string): void {
        this._src = src;
        this._sceneObjectData.src = src;
    }

    /** Whether this object is active (enabled) in the scene, a cue's or state's override included. */
    get active(): boolean {
        return this._cueActive ?? this._activeOverride ?? this._resolvedActive;
    }

    /** The authored active flag after `$ref` resolution, whatever a state shows. */
    get authoredActive(): boolean {
        return this._resolvedActive;
    }

    /**
     * Effective active state: own enabled ∧ every ancestor's — i.e. whether
     * this object is actually visible-eligible in the scene, no matter WHO
     * hid it (own `active`, a state's `active: false`, a page flip hiding an
     * ancestor, a script). Tracked via Babylon's enabled-state observable, so
     * every `setEnabled` path funnels through one notification point; flips
     * dispatch `onEffectiveActiveChanged` to this object's behaviors (the
     * activation lifecycle — CANOPY_STATES §6.2 / AnimationSequence).
     *
     * The active ladder, in one place: {@link authoredActive} is the authored
     * `active` after `$ref` resolution (what serializes); {@link active} is
     * this object's OWN flag, a Timeline cue's over a state's over the authored one;
     * `effectiveActive` is that flag combined with every ancestor's (what the
     * user can actually see). Babylon's
     * `transform.isEnabled(false)` / `isEnabled()` are the underlying
     * own/effective reads.
     */
    get effectiveActive(): boolean {
        return this._effectiveActive;
    }

    /**
     * Set the active (enabled) state of this object.
     * Updates both the serializable data and the Babylon transform.
     * Replaces any `$name` reference with the concrete boolean, and clears a
     * state's and a cue's override: the last writer wins.
     */
    setActive(active: boolean): void {
        this._sceneObjectData.active = active;
        this._resolvedActive = active;
        this._activeOverride = undefined;
        this._cueActive = undefined;
        this._propertyNames = this._scanSceneObjectPropertyNames();
        this.transform.setEnabled(active);
    }

    /**
     * Show or hide this object for a state without touching authored data.
     * `undefined` hands visibility back to the authored flag. A state that
     * declares a new value takes over from a cue that spoke before it.
     */
    setActiveOverride(active: boolean | undefined): void {
        // Even the value it already holds: switching to a second state that
        // hides must still win over a cue that showed the object since.
        const takesOver = active !== undefined && this._cueActive !== undefined;
        if (active === this._activeOverride && !takesOver) return;
        this._activeOverride = active;
        if (takesOver) this._cueActive = undefined;
        this.transform.setEnabled(this.active);
    }

    /**
     * Show or hide this object for a Timeline cue without touching authored
     * data or a state's override. A cue ranks over a state so it can show what
     * a state hides, and each writer releases only its own layer: `undefined`
     * hands visibility back to whatever the state and the authored flag say.
     * Once a state or `setActive` has taken over, the cue is not re-applied
     * until the Timeline writes again (its next cue, a seek, play or loop).
     */
    setActiveCue(active: boolean | undefined): void {
        if (active === this._cueActive) return;
        this._cueActive = active;
        this.transform.setEnabled(this.active);
    }

    /**
     * Set position, optionally with a transition.
     * @param transition - If provided, interpolates from current. If omitted or
     *   `null`, snaps. Only the position channel is affected — in-flight
     *   rotation/scale transitions continue untouched (KHR per-pointer rule);
     *   an eased re-write replaces the running position transition, a snap
     *   cancels it.
     */
    setPosition(value: Vector3, transition?: TransitionConfig | null): void {
        this._startPositionChannel(value, transition);
    }

    /**
     * Set rotation (quaternion), optionally with a transition.
     * @param transition - If provided, interpolates via slerp. If omitted or
     *   `null`, snaps. Only the rotation channel is affected — in-flight
     *   position/scale transitions continue untouched (KHR per-pointer rule);
     *   an eased re-write replaces the running rotation transition, a snap
     *   cancels it.
     */
    setRotation(value: Quaternion, transition?: TransitionConfig | null): void {
        this._startRotationChannel(value, transition);
    }

    /**
     * Set scale, optionally with a transition.
     * @param transition - If provided, interpolates from current. If omitted or
     *   `null`, snaps. Only the scale channel is affected — in-flight
     *   position/rotation transitions continue untouched (KHR per-pointer
     *   rule); an eased re-write replaces the running scale transition, a snap
     *   cancels it.
     */
    setScale(value: Vector3, transition?: TransitionConfig | null): void {
        this._startScaleChannel(value, transition);
    }

    /**
     * Set position, rotation, and scale together.
     * Starts one transition channel per provided component with the same
     * duration/easing (lockstep — visually identical to a single combined
     * tween). Omitted components are left entirely alone: their current
     * values AND any in-flight transitions are preserved.
     */
    setTransform(
        target: { position?: Vector3; rotation?: Quaternion; scale?: Vector3 },
        transition?: TransitionConfig | null,
    ): void {
        if (target.position) this._startPositionChannel(target.position, transition);
        if (target.rotation) this._startRotationChannel(target.rotation, transition);
        if (target.scale) this._startScaleChannel(target.scale, transition);
    }

    /**
     * Rename this object. Updates all internal name locations atomically.
     * @internal Used by TraceScene.renameSceneObject() — callers should use that API instead.
     */
    _setName(newName: string): void {
        this.name = newName;
        this._sceneObjectData.name = newName;
        this.transform.name = newName;
        if (this._animationRoot) {
            this._animationRoot.name = `${newName}__animRoot`;
        }
        if (this._animationLayer) {
            this._animationLayer.name = `${newName}__animLayer`;
        }
    }

    /** Access to resource manager for loading assets */
    get resources(): ResourceManager {
        return this._resources;
    }

    /** Access to scene configuration */
    get config(): ConfigData {
        return this._config;
    }

    /**
     * Animation clips from the instantiated model, keyed by clean clip name.
     * Populated by the Model behavior during load.
     */
    get animationClips(): ReadonlyMap<string, AnimationGroup> {
        if (this._boundAnimationClips.size === 0) return this._animationClips;
        if (!this._mergedAnimationClips) {
            // TRACE_animation_binding §2.4: the file's own animations first, then
            // the bound ones; a name addresses the first it names.
            const merged = new Map(this._animationClips);
            for (const [id, group] of this._boundAnimationClips) {
                if (!merged.has(id)) merged.set(id, group);
            }
            this._mergedAnimationClips = merged;
        }
        return this._mergedAnimationClips;
    }

    /**
     * The clips of this object's OWN file only — without the ones
     * `TRACE_animation_binding` bound to it. What plays by default (a model with
     * no `Animation` behavior loops its first own clip in play mode); a bound
     * clip never autoplays.
     */
    get ownAnimationClips(): ReadonlyMap<string, AnimationGroup> {
        return this._animationClips;
    }

    /** The clips `TRACE_animation_binding` bound to this object, by source animation name. */
    get boundAnimationClips(): ReadonlyMap<string, AnimationGroup> {
        return this._boundAnimationClips;
    }

    /**
     * Replace the bound clips (see `loaders/animationBinding`). Attached
     * `Animation` behaviors switch to the new set, then the previous set is
     * disposed: bound groups belong to this object. Bound clips are
     * NEVER trimmed (`_trimLeadingDeadFrames`): the binder builds them from
     * the source's own keys (no loader padding to undo), and their time is
     * the source file's time — RECORDINGS_SPEC §4.2, DESIGN_PRINCIPLES §10.10.
     * @internal
     */
    _setBoundAnimationClips(clips: ReadonlyMap<string, AnimationGroup>): void {
        const previous = this._boundAnimationClips;
        this._boundAnimationClips = clips;
        this._mergedAnimationClips = null;
        // Attached Animation behaviors hold the groups they registered on
        // attach: hand them the new ones before the old are disposed.
        if (previous.size > 0) {
            for (const behavior of this.behaviors) {
                if (behavior.name === 'Animation') (behavior as Animation)._onBoundClipsReplaced(previous);
            }
        }
        for (const group of previous.values()) {
            if (![...clips.values()].includes(group)) group.dispose();
        }
    }

    /**
     * `TRACE_animation_binding` on this object's node, as authored (the
     * internal record — do not mutate). Undefined when it has none.
     */
    get animationBinding(): Readonly<AnimationBindingData> | undefined {
        return this._sceneObjectData.animationBinding;
    }

    /**
     * Store animation clips from instantiated model entries.
     * Strips the optional name prefix to produce clean clip names.
     *
     * Also trims leading dead frames: Babylon's GLTF loader calls
     * `normalize(0)` which pads animations that start at a non-zero time
     * with a constant hold at frame 0. This causes models to freeze for
     * seconds before the actual motion begins. We detect this by checking
     * whether every targeted animation's first two keyframes share the
     * same value (the signature of normalize's padding) and advance the
     * group's `from` past the dead segment.
     *
     * The trim only moves where play() starts a clip. A Timeline samples
     * clips on their own glTF time from frame 0 (`Animation.sampleAt`), and
     * clips bound by `TRACE_animation_binding` are never trimmed.
     *
     * @param groups - Raw animation groups from instantiateModelsToScene
     * @param prefix - Optional prefix to strip (e.g. `"ObjectName_"`)
     */
    setAnimationClips(groups: readonly AnimationGroup[], prefix?: string): void {
        const map = new Map<string, AnimationGroup>();
        const driven: AnimationGroup[] = [];
        for (const group of groups) {
            // A driven animation (`extras.trace.driver`) is never played by
            // time, so it is not a clip — it is evaluated from its driver.
            if (getAnimationDriver(group)) {
                driven.push(group);
                continue;
            }
            const clipName = prefix && group.name.startsWith(prefix)
                ? group.name.slice(prefix.length)
                : group.name;
            SceneObject._trimLeadingDeadFrames(group);
            map.set(clipName, group);
        }
        this._animationClips = map;
        this._drivenAnimationClips = driven;
        this._mergedAnimationClips = null;
    }

    /**
     * The file's DRIVEN animations (`extras.trace.driver`): never in
     * {@link animationClips}, never played by time; evaluated every frame at
     * the value of their driver (`loaders/animationBinding/drivenAnimations`).
     */
    get drivenAnimationClips(): readonly AnimationGroup[] {
        return this._drivenAnimationClips;
    }

    /**
     * Detect and skip the constant-hold segment that Babylon's GLTF loader
     * inserts via `normalize(0)`.  If every targeted animation has its first
     * two keys at frames 0 and N (N > 0) with identical values, advance the
     * group's `from` to N so playback begins at the real motion.
     */
    private static _trimLeadingDeadFrames(group: AnimationGroup): void {
        if (group.from !== 0) return;

        const targeted = group.targetedAnimations;
        if (targeted.length === 0) return;

        let realStart = Infinity;

        for (const ta of targeted) {
            const keys = ta.animation.getKeys();
            if (keys.length < 2) return; // can't determine, bail
            if (keys[0].frame !== 0) return; // not padded

            const secondFrame = keys[1].frame;
            if (secondFrame <= 0) return; // no gap

            // Check if the first key's value is identical to the second's.
            // Values may be numbers, Vector3, Quaternion, or other Babylon types.
            if (!SceneObject._valuesEqual(keys[0].value, keys[1].value)) {
                return; // genuine hold — don't trim
            }

            realStart = Math.min(realStart, secondFrame);
        }

        if (realStart > 0 && realStart < Infinity) {
            group.from = realStart;
        }
    }

    /** Deep-equal comparison for animation key values (numbers, vectors, quaternions). */
    private static _valuesEqual(a: unknown, b: unknown): boolean {
        if (a === b) return true;
        if (typeof a === 'number' && typeof b === 'number') return a === b;
        // Babylon Vector3/Quaternion/Color etc. all have equals()
        if (a != null && b != null && typeof (a as { equals?: (v: unknown) => boolean }).equals === 'function') {
            return (a as { equals: (v: unknown) => boolean }).equals(b);
        }
        return false;
    }

    /** Access to TraceScene for cross-object resolution */
    get traceScene(): TraceScene {
        return this._traceScene;
    }

    /**
     * Get the fully qualified transform path (FQTP) for this object.
     * Example: "Scene/UI/Panel/Button"
     */
    getPath(): string {
        return this._traceScene.getFullPath(this) ?? this.name;
    }

    constructor(
        sceneData: SceneObjectData,
        babylonScene: Scene,
        resources: ResourceManager,
        config: ConfigData,
        traceScene: TraceScene,
        eventTarget?: SceneEventSystem,
        preloadedTransform?: TransformNode
    ) {
        // Deep clone sceneData at the boundary to ensure it's mutable
        // (trace-editor may pass frozen objects from Immer middleware)
        this._sceneObjectData = structuredClone(sceneData);
        this.babylonScene = babylonScene;
        this._resources = resources;
        this._config = config;
        this._traceScene = traceScene;
        this._eventTarget = eventTarget || null;
        this.name = sceneData.name;
        this.behaviors = [];
        this._src = sceneData.src || ""; // Default to empty string if undefined
        if (preloadedTransform) {
            this.transform = preloadedTransform;
            this._installParentInterceptor();
            this._installEffectiveActiveTracking();
        } else {
            this.setupTransformRoot();
        }

        // Scan for $name references and resolve active/src/transform fields.
        // For non-preloaded transforms, setupTransformRoot() already applied
        // resolved values. For preloaded transforms, we need to apply here.
        this._scanAndResolveProperties();
        if (preloadedTransform && this._propertyNames.size > 0) {
            if (!this._resolvedActive) {
                this.transform.setEnabled(false);
            }
        }
    }

    /**
     * Create a SceneObject from a pre-loaded TransformNode.
     * Used when plugins have already loaded the geometry into the scene.
     *
     * @param transform - Already-loaded TransformNode from plugin
     * @param name - Object name
     * @param babylonScene - The Babylon.js scene
     * @param resources - Resource manager
     * @param config - Configuration data
     * @param traceScene - The TraceScene this object belongs to
     * @param options - Optional: behaviors, active state, src
     * @param eventTarget - Optional event target for notifications
     */
    static fromLoadedTransform(
        transform: TransformNode,
        name: string,
        babylonScene: Scene,
        resources: ResourceManager,
        config: ConfigData,
        traceScene: TraceScene,
        options?: {
            behaviors?: BehaviorData[];
            /** `boolean`, or the binding string that computes it. */
            active?: boolean | string;
            src?: string;
            extensionsUsed?: string[];
            resetAssetTransform?: boolean;
            /** `src` is a reference to another scene document (spec §3.4). */
            sceneRef?: boolean;
            /** `files[k].name` — the file's original human name (spec §2.1). */
            srcName?: string;
            /**
             * glTF 2.1 node identity read off the document. The session
             * decides whether it is kept, replaced or dropped — see
             * `TraceScene._stampNodeUid`.
             */
            uid?: string;
            /** The loader already put `src` in the scene — see {@link SceneObject._assetInstantiated}. */
            assetInstantiated?: boolean;
            /**
             * The document node's foreign glTF payload, kept verbatim for the
             * next save (issue #834, CONTENT_MODEL_SPEC §3.6).
             */
            foreign?: ForeignNodeData;
            /** `TRACE_animation_binding` on the document node, as the session holds it. */
            animationBinding?: AnimationBindingData;
        },
        eventTarget?: SceneEventSystem
    ): SceneObject {
        // Build minimal SceneData from loaded node
        const sceneData: SceneObjectData = {
            name,
            active: options?.active ?? true,
            behaviors: options?.behaviors ?? [],
            src: options?.src,
            // Nothing here reads it — it rides along in the object's data so
            // the writer can put it back (issue #834).
            ...(options?.foreign ? { foreign: options.foreign } : {}),
            // The document's own node identity, as read (issue #837).
            ...(options?.uid ? { uid: options.uid } : {}),
            ...(options?.animationBinding ? { animationBinding: options.animationBinding } : {}),
            transform: {
                // Extract current transform from node (already applied by plugin)
                translation: [transform.position.x, transform.position.y, transform.position.z],
                rotation: transform.rotationQuaternion
                    ? [transform.rotationQuaternion.x, transform.rotationQuaternion.y, transform.rotationQuaternion.z, transform.rotationQuaternion.w]
                    : undefined,
                scale: [transform.scaling.x, transform.scaling.y, transform.scaling.z],
            },
        };

        // Create SceneObject with pre-loaded transform (skips setupTransformRoot)
        const sceneObject = new SceneObject(sceneData, babylonScene, resources, config, traceScene, eventTarget, transform);
        sceneObject._src = options?.src || "";
        sceneObject.extensionsUsed = options?.extensionsUsed;
        sceneObject._resetAssetTransform = options?.resetAssetTransform ?? false;
        sceneObject._sceneRef = options?.sceneRef ?? false;
        sceneObject._srcName = options?.srcName;
        sceneObject._assetInstantiated = options?.assetInstantiated ?? false;

        // Apply active state
        if (options?.active === false) {
            transform.setEnabled(false);
        }

        return sceneObject;
    }

    public setupTransformRoot(parent?: TransformNode | null): void {
        const transformNode = new TransformNode(this._sceneObjectData.name, this.babylonScene);

        // Set parent and assign transform
        if (parent) {
            transformNode.parent = parent;
        }
        this.transform = transformNode;
        this._installParentInterceptor();
        this._installEffectiveActiveTracking();

        if (this._sceneObjectData.transform) {
            applyTransform(this._sceneObjectData.transform, transformNode);
        }

        // Apply initial active state immediately so disabled objects don't briefly appear
        if (!this._resolvedActive) {
            this.transform.setEnabled(false);
        }
    }

    /**
     * This object's glTF 2.1 node identity, when it has one (issue #837,
     * SCENE_ROOT_SPEC §2.3 "Identity").
     *
     * Minted on create in a 2.1 session, read off `node.uid` when the object
     * came from a 2.1 document, fresh on duplicate and on unpack. Undefined in
     * a legacy session, and for an object whose document said nothing about
     * identity. Nothing resolves by it yet.
     */
    public get uid(): string | undefined {
        return this._sceneObjectData.uid;
    }

    /**
     * Set (or clear) this object's node identity.
     *
     * @internal The one writer is {@link TraceScene}, at the three moments
     *   SCENE_ROOT_SPEC §2.3 names: create, duplicate, unpack. A host must
     *   never re-stamp an object — an identity that changes under an editor is
     *   worse than none.
     */
    _setUidInternal(uid: string | undefined): void {
        if (uid === undefined) {
            delete this._sceneObjectData.uid;
        } else {
            this._sceneObjectData.uid = uid;
        }
    }

    /**
     * Get a defensive deep clone of this object's scene data.
     * Callers may freely mutate the returned object without affecting internal state.
     * To apply changes, pass the modified data to {@link setData}.
     */
    getSceneObjectData(): SceneObjectData {
        return structuredClone(this._sceneObjectData);
    }

    /**
     * Register a pre-created behavior and add its data to the scene data.
     * @internal Used by {@link addBehavior} for script behaviors.
     */
    private _registerBehaviorWithData(behavior: TraceBehavior, data: BehaviorData): void {
        if (!this._sceneObjectData.behaviors) {
            this._sceneObjectData.behaviors = [];
        }
        this._sceneObjectData.behaviors.push(data);
        this.behaviors.push(behavior);
        this._traceScene?._bumpStructureRevision?.();
        this.transform.addBehavior(behavior, true);
    }

    /**
     * Get the parent path from the serialized transform data, if any.
     * @internal Used during scene loading to resolve parent hierarchy.
     */
    _getParentPath(): string | undefined {
        return this._sceneObjectData.transform?.parent;
    }

    /**
     * Get the raw behavior data array from scene data.
     * Returns the internal reference (not a clone) for performance during loading.
     * @internal For viewer code that reads the authored data without a clone: loading, migration and the editor's scene reads.
     */
    _getBehaviorData(): BehaviorData[] | undefined {
        return this._sceneObjectData.behaviors;
    }

    /** Nesting depth of open behavior-load windows (see {@link isLoadingBehaviors}). */
    private _behaviorLoadDepth = 0;

    /**
     * True while this object's behaviors are still being built from its
     * behavior DATA.
     *
     * The object is already in the scene graph by then: every caller parents
     * it first and loads afterwards, because a behavior's `load()` needs the
     * transform. So there is a window, as long as the slowest download on the
     * object, in which it is a findable child of the scene while
     * `getBehavior()` answers null for everything it declares.
     *
     * Anything that reads behaviors to decide how to treat an object has to
     * ask this first, or it will treat "still loading" as "declares nothing".
     * Registration is per-behavior and failure-tolerant, so "declared but not
     * registered" is NOT the same question and cannot answer it: a behavior
     * whose asset 404s is dropped for good. When the window closes the scene
     * says so ({@link TraceScene.onBehaviorsLoaded}), so a reader that held
     * off can look again.
     *
     * The window opens wherever behaviors are built from data, which is not
     * the same as "this object is new": on `reload()` and on import too. A
     * loader path that parents a batch and only builds it after an
     * intervening await opens it earlier still, at the parenting, which is
     * why the two methods below are counted rather than a boolean.
     */
    get isLoadingBehaviors(): boolean {
        return this._behaviorLoadDepth > 0;
    }

    /**
     * Open the loading window. Counted rather than a boolean: `reload()` may
     * be called again while an earlier one is still awaiting its downloads.
     * @internal Paired with {@link _endBehaviorLoad} by whatever builds behaviors from data.
     */
    _beginBehaviorLoad(): void {
        this._behaviorLoadDepth++;
    }

    /**
     * @internal Closes the window opened by {@link _beginBehaviorLoad}; the
     * last close tells the scene.
     */
    _endBehaviorLoad(): void {
        if (this._behaviorLoadDepth === 0) return;
        this._behaviorLoadDepth--;
        // Optional call: stub scenes in tests omit it, like _emitTopologyChanged.
        if (this._behaviorLoadDepth === 0) this._traceScene?._emitBehaviorsLoaded?.(this);
    }

    /**
     * Replace the behavior data array in scene data.
     * @internal Used by SceneCreation during migration.
     */
    _setBehaviorData(behaviors: BehaviorData[]): void {
        this._sceneObjectData.behaviors = behaviors;
    }

    /**
     * Get the raw authored transform from scene data.
     * Returns the internal reference (not a clone), so a load-boundary
     * migration can normalize authored TRS in place — {@link getSceneObjectData}
     * hands back a defensive copy and would silently drop the write.
     * @internal For load-boundary migration, and for a runtime writer that
     * owns an authored field and keeps it current.
     */
    _getTransformData(): TransformData | undefined {
        return this._sceneObjectData.transform;
    }

    /**
     * Add a behavior to this SceneObject. Accepts either a behavior class
     * (type-safe) or an @type string (works for both built-in and script types).
     * Throws if a behavior of this type is already attached — use {@link setBehavior}
     * for idempotent add-or-update semantics.
     *
     * ```typescript
     * const anim = await obj.addBehavior(Animation);
     * const anim = await obj.addBehavior(Animation, { clip: 'Idle' });
     * await obj.addBehavior('MyScript', { speed: 2 });
     * ```
     */
    async addBehavior<T extends TraceBehavior>(type: BehaviorConstructor<T>, overrides?: Partial<Omit<DataOf<T>, '@type'>>): Promise<T>;
    async addBehavior(type: string, overrides?: Partial<Record<string, unknown>> | BehaviorData): Promise<TraceBehavior>;
    async addBehavior<T extends TraceBehavior>(
        type: BehaviorConstructor<T> | string,
        overrides?: Partial<Record<string, unknown>> | BehaviorData,
    ): Promise<TraceBehavior> {
        // String overload — resolve type name, then delegate or handle scripts
        if (typeof type === 'string') {
            const Ctor = getBehaviorConstructor(type);
            if (Ctor) return this.addBehavior(Ctor, overrides);

            // Script behavior
            if (this.getBehavior(type)) {
                throw new Error(`[SceneObject] ${this.name} already has a ${type} behavior.`);
            }
            const sb = this.traceScene.scriptRegistry?.createBehavior(this, type, overrides);
            if (sb) {
                this._registerBehaviorWithData(sb, sb.getData());
                Logger.log(`[SceneObject] Added script ${type} to ${this.name}`);
                return sb;
            }

            throw new Error(`[SceneObject] Unknown behavior type: ${type}`);
        }

        // Class overload — built-in behavior
        const typeName = type.behaviorType;

        if (this.getBehavior(type)) {
            throw new Error(`[SceneObject] ${this.name} already has a ${typeName} behavior. Use setBehavior() to update it.`);
        }

        const data = createDefaultData<DataOf<T>>(typeName, overrides);

        // Add to sceneData.behaviors for persistence
        if (!this._sceneObjectData.behaviors) {
            this._sceneObjectData.behaviors = [];
        }
        this._sceneObjectData.behaviors.push(data);

        // Create, load resources, register, and attach to Babylon
        const behavior = new type(this, data);
        await behavior.load();
        this.behaviors.push(behavior);
        this._traceScene?._bumpStructureRevision?.();
        this.transform.addBehavior(behavior, true);
        this.onBehaviorAddedObservable.notifyObservers(behavior);

        // Optional call: stub scenes in tests omit it, like _emitTopologyChanged.
        this.traceScene.refreshMeshPickability?.(this);
        Logger.log(`[SceneObject] Added ${typeName} behavior to ${this.name}`);
        return behavior;
    }

    /**
     * Add-or-update a behavior. If the behavior already exists, merges the
     * overrides into its current data and commits. If it doesn't exist, adds
     * it with schema defaults + overrides. Handles reload internally.
     *
     * Accepts a behavior class (type-safe) or an @type string.
     *
     * ```typescript
     * await obj.setBehavior(Renderer, { castShadows: true });
     * await obj.setBehavior('Text', { text: 'Hello' });
     * ```
     *
     * @returns The behavior instance (existing or newly created)
     */
    async setBehavior<T extends TraceBehavior>(BehaviorClass: BehaviorConstructor<T>, overrides: Partial<Omit<DataOf<T>, '@type'>>): Promise<T>;
    async setBehavior(type: string, overrides: Partial<Record<string, unknown>>): Promise<TraceBehavior>;
    async setBehavior<T extends TraceBehavior>(
        type: BehaviorConstructor<T> | string,
        overrides: Partial<Record<string, unknown>>,
    ): Promise<TraceBehavior> {
        if (typeof type === 'string') {
            const existing = this.getBehavior(type);
            if (existing) {
                await existing.commit(overrides);
                return existing;
            }
            return this.addBehavior(type, overrides);
        }
        const existing = this.getBehavior(type);
        if (existing) {
            await existing.commit(overrides);
            return existing;
        }
        return this.addBehavior(type, overrides as Partial<Omit<DataOf<T>, '@type'>>);
    }

    /**
     * Register a pre-created behavior instance (used during scene loading).
     * Does NOT add to sceneData.behaviors — the data is already there.
     * @internal
     */
    _registerBehavior(behavior: TraceBehavior): void {
        this.behaviors.push(behavior);
        this._traceScene?._bumpStructureRevision?.();
    }

    /**
     * Get behavior by class (type-safe) or by @type string (dynamic).
     *
     * ```typescript
     * const anim = obj.getBehavior(Animation);       // typed as Animation | null
     * const b    = obj.getBehavior('Interactions');   // typed as TraceBehavior | null
     * ```
     */
    getBehavior<T extends TraceBehavior>(type: abstract new (...args: never) => T): T | null;
    getBehavior(type: 'Animation'): Animation | null;
    getBehavior(type: 'Properties'): Properties | null;
    getBehavior(type: 'Layout'): Layout | null;
    getBehavior(type: 'Frame'): Frame | null;
    getBehavior(type: string): TraceBehavior | null;
    getBehavior<T extends TraceBehavior>(type: (abstract new (...args: never) => T) | string): T | TraceBehavior | null {
        if (typeof type === 'string') {
            for (const behavior of this.behaviors) {
                if (behavior.name === type) {
                    return behavior;
                }
            }
            return null;
        }
        for (const behavior of this.behaviors) {
            if (behavior instanceof type) {
                return behavior;
            }
        }
        return null;
    }

    /**
     * Get all behaviors attached to this SceneObject.
     * Returns a defensive copy of the behaviors array.
     */
    getBehaviors(): TraceBehavior[] {
        return [...this.behaviors];
    }

    /**
     * Resolve a property by name via ancestor tree walk.
     * Walks up the scene hierarchy and returns the value from the first
     * Properties behavior that defines the given name.
     *
     * @param name - Property name to resolve (e.g., "color", "label")
     * @returns The property value, or undefined if no ancestor defines it
     */
    resolveProperty(name: string, settled = false): PropertyValue | undefined {
        let current: SceneObject | null = this;
        while (current) {
            const props = current.getBehavior('Properties');
            if (props?.hasProperty(name)) {
                return props.getValue(name, settled);
            }
            current = current.getParent();
        }
        return undefined;
    }

    /**
     * Serialize all behaviors for export.
     *
     * The scene root's behaviors reach the wire through here, and there is
     * exactly one destination for them: the document, at
     * `scenes[n].extras.trace.behaviors` (CONTENT_MODEL_SPEC §3.6). They are
     * never serialized into the CMS `metadata` payload — see
     * `editor/PublishDecision.ts`, which ignores what it is handed.
     *
     * @param options.excludeCmsManaged - When true, behaviors whose constructor
     *   has a `cmsManaged === true` static (e.g. {@link ImageAnchor}) are
     *   omitted. Use this for file export where CMS-managed data must not
     *   appear. Defaults to false: callers that consume authored data
     *   (refreshSceneData) still see every behavior.
     * @returns ComponentData[] — same format as SceneData.components
     */
    serializeBehaviors(options: { excludeCmsManaged?: boolean } = {}): BehaviorData[] {
        const filtered = options.excludeCmsManaged
            ? this.behaviors.filter(b => !isCmsManagedBehavior(b))
            : this.behaviors;
        return filtered.map(b => b.serialize());
    }

    /**
     * Remove a behavior by class (type-safe) or @type string (dynamic).
     * The behavior is immediately detached and removed from both
     * the runtime array and sceneData.behaviors.
     *
     * ```typescript
     * obj.removeBehavior(Animation);        // by class
     * obj.removeBehavior('MyScript');       // by @type string
     * ```
     *
     * @returns true if a behavior was removed, false otherwise
     */
    removeBehavior<T extends TraceBehavior>(type: abstract new (...args: never) => T): boolean;
    removeBehavior(type: string): boolean;
    removeBehavior<T extends TraceBehavior>(type: (abstract new (...args: never) => T) | string): boolean {
        const index = typeof type === 'string'
            ? this.behaviors.findIndex(b => b.name === type)
            : this.behaviors.findIndex(b => b instanceof type);
        if (index === -1) return false;

        const behavior = this.behaviors[index];
        const behaviorType = behavior.name;

        // Remove from Babylon's behavior system
        this.transform.removeBehavior(behavior);

        // Detach (dispose resources)
        behavior.detach();

        // Remove from runtime array
        this.behaviors.splice(index, 1);
        this._traceScene?._bumpStructureRevision?.();

        // Remove from sceneData.behaviors for persistence
        if (this._sceneObjectData.behaviors) {
            const dataIndex = this._sceneObjectData.behaviors.findIndex(
                (b: BehaviorData) => b['@type'] === behaviorType
            );
            if (dataIndex !== -1) {
                this._sceneObjectData.behaviors.splice(dataIndex, 1);
            }
        }

        this.traceScene.refreshMeshPickability?.(this);
        return true;
    }

    /**
     * Fully reload this SceneObject.
     * Destroys and recreates all behaviors from sceneData.
     *
     * **Important**: After reload(), all behavior references are stale.
     * Re-fetch behaviors via getBehavior() after reload completes.
     *
     * Emits 'sceneobject:reloaded' event on the eventTarget element with
     * `{ sceneObject, name }` detail.
     *
     * @example
     * ```typescript
     * // Typically called via setData() for full object reconstruction:
     * await sceneObject.setData(newSceneData);
     * // All behavior references are now stale — re-fetch after reload.
     * const newFrame = sceneObject.getBehavior(Frame);
     * ```
     */
    async reload(): Promise<void> {

        Logger.log(`[SceneObject] Reload ${this.name}`);

        // Capture current runtime transform before destroying
        this.captureTransform();

        // Flatten animation root first so its children become direct children
        this._flattenAnimationRoot();

        // Capture only child SceneObject transforms (not model meshes/internal nodes).
        // Model meshes will be recreated by the Model behavior; only SceneObject children survive reload.
        const allChildren = this.transform.getChildren<TransformNode>();
        const children = allChildren.filter(child =>
            this._traceScene.findSceneObjectByTransform(child) !== undefined
        );
        const parent = this.transform.parent as TransformNode | null;

        // Capture sibling index to preserve order after recreating the transform
        let siblingIndex = -1;
        if (parent) {
            const siblings = parent.getChildren();
            siblingIndex = siblings.indexOf(this.transform);
        }

        // Destroy existing scene object, passing child SceneObjects through as they will not be destroyed
        this.destroy(children);
        // Whatever the loader instantiated went with it: from here the "src
        // as template" rule owns the file (a Model loads it), or a reloaded
        // inline-loaded object — an avatar whose src just changed — would
        // come back with no geometry and nothing bound to.
        this._assetInstantiated = false;

        // Recreate transform root with parent already set (prevents flash at origin)
        this.setupTransformRoot(parent);

        // Restore sibling order: the new transform was appended at end,
        // re-parent siblings that were after us to push them back after.
        if (parent && siblingIndex >= 0) {
            const siblings = parent.getChildren<TransformNode>();
            // Siblings that should come after us (those originally at siblingIndex+)
            // are now at indices siblingIndex..end-1 (our new transform is at end).
            const toReorder = siblings.slice(siblingIndex, siblings.length - 1);
            for (const sib of toReorder) {
                sib.parent = null;
                sib.parent = parent;
            }
        }

        // Child SceneObjects go onto the new transform before the load awaits:
        // frames render during it, and a child left unparented is drawn at its
        // local pose from the scene origin. An animation root created during
        // the load takes them along (getOrCreateAnimationRoot).
        for (const child of children) child.parent = this.transform;

        // applyComponents handles everything: built-in primitives, asset-backed
        // primitives (Model/Image/Video from src), and all other behaviors.
        if (!_applyComponents) throw new Error('[SceneObject] setApplyComponents() was never called — ensure SceneCreation is imported before reloading objects.');
        await _applyComponents(this, this.name);

        this._attachBehaviors();

        // Emit event
        this._eventTarget?.dispatchEvent(new CustomEvent('sceneobject:reloaded', {
            bubbles: true,
            detail: { sceneObject: this, name: this.name }
        }));
    }

    /**
     * Attach all registered behaviors to Babylon's behavior system.
     *
     * Uses `attachImmediately=true` because Babylon's `addBehavior()` defers
     * `attach()` while `scene.isLoading` is true (i.e. pending data tokens
     * exist). Pending data tokens come from background texture uploads inside
     * loaded GLBs (e.g. Frame's PBR material textures), which are unrelated
     * to our behavior readiness. Our two-phase loading pattern guarantees
     * all `behavior.load()` calls have resolved before this method is called,
     * so behaviors are fully ready to attach. Without `attachImmediately`,
     * `onAttach()` is deferred and inter-behavior wiring breaks (e.g. Frame
     * never reads Image's aspect ratio).
     *
     * @internal Used by TraceScene during scene loading and by reload().
     */
    _attachBehaviors(): void {
        for (const behavior of this.behaviors) {
            this.transform.addBehavior(behavior, true);
        }

        // If the model has animation clips but no Animation behavior to manage them,
        // auto-play the first clip looping so animated models look alive by default.
        // Only start if already in play mode — otherwise playback begins when
        // play mode is entered via TraceScene.setPlayMode().
        if (this._traceScene.isPlayMode() && this._animationClips.size > 0 && !this.getBehavior('Animation')) {
            const firstGroup = this._animationClips.values().next().value;
            if (firstGroup) {
                firstGroup.start(true);
            }
        }

        this.setupInspectableProperties();
    }

    addChild(transform: TransformNode): void {
        Logger.log(
            `scene object named ${this.name} added child ${transform.name}`
        );
        transform.parent = this._animationRoot || this.transform;
    }

    /** Where a move changed the hierarchy: this object, and the parent it left if it had one. Undefined when that parent is no scene object. */
    private _changedAt(prevParent: Node | null): SceneObject[] | undefined {
        if (!prevParent) return [this];
        const left = this._traceScene?.findSceneObjectByTransform?.(prevParent);
        return left ? [this, left] : undefined;
    }

    /** @internal Whether a parent change is waiting to settle, its report still owed. */
    get _reparentPending(): boolean {
        return this._reparentSettlePending;
    }

    setParent(parent: SceneObject): void {
        const prevParent = this.transform.parent;
        this.transform.parent = parent._animationRoot || parent.transform;
        this._notifyHierarchyChanged();
        this._traceScene?._emitTopologyChanged?.(this._changedAt(prevParent));
        if (this.transform.parent !== prevParent) {
            this._traceScene?._emitObjectReparented?.(this, prevParent);
        }
    }

    /**
     * Install an interceptor on the transform's setParent method so that
     * external reparenting (e.g. Inspector drag-and-drop) triggers property
     * re-subscription and sibling name dedup. Also consults the scene's
     * reparent policy and reverts the change if vetoed (used by CMS scene
     * mode to enforce flat scenes). Called once after this.transform is
     * assigned.
     *
     * A node can pass through SEVERAL parents inside one synchronous block and
     * come to rest exactly where it began. Babylon's gizmos do it constantly:
     * `BoundingBoxGizmo` runs `anchor.addChild(node)` -> `anchor.removeChild(node)`
     * -> `node.setParent(original)` to measure a node on attach, and
     * `setParent(null)` -> `setParent(original)` again inside every
     * `updateBoundingBox()`. That is a BORROW, not a move, and this interceptor
     * used to believe all five hops.
     *
     * So the work is split by what a single hop can honestly answer:
     *
     *   SYNCHRONOUS, because callers read the result on the next line — the
     *     policy veto (it reverts, so it must answer before returning), name
     *     dedup (`reparentSceneObjectPreservingWorld` returns the deduped name;
     *     the Layers drag captures the post-dedup path for its undo) and,
     *     on a hop under a parent of this scene, the authored pose, which a
     *     save right after the move reads ({@link _carryAuthoredPose}; in
     *     Play only the pose a behavior holds, never authored data).
     *
     *   DEFERRED to {@link _settleReparent}, because "did this object move?" is
     *     not answerable until the block ends — $ref resubscription, the
     *     topology signal and the reparent event. Those are the expensive,
     *     host-visible ones, and they are what made a borrow a catastrophe:
     *     hosts re-solve layouts and re-sync selection surfaces on them, and
     *     re-syncing re-attaches the gizmo, which borrows the node again
     *     (TraceFrontend#2270 — the browser tab died on a doodad). The
     *     authored pose of a move that rests at the bare root waits too: only
     *     the settle knows the move rests there.
     *
     *   SIBLING ORDER, restored on the hop that brings the node back. Babylon
     *     re-appends a returning node as the LAST child, and child order is
     *     data (Layout flow, export), so a borrow used to reorder its siblings
     *     (#783). See {@link restoreChildSlot}.
     */
    private _installParentInterceptor(): void {
        const sceneObject = this;
        const originalSetParent = this.transform.setParent.bind(this.transform);
        this.transform.setParent = function (
            node: Node | null,
            preserveScalingSign?: boolean,
            updatePivot?: boolean,
        ): TransformNode {
            const prevParent = sceneObject.transform.parent;
            const firstHop = !sceneObject._reparentSettlePending;
            // The order this node is about to leave, before Babylon takes it
            // out. Never at the bare root: scene.rootNodes caches each node's
            // index, and nothing reads root order anyway.
            const left = prevParent && node !== prevParent
                ? { parent: prevParent, order: prevParent.getChildren() }
                : null;
            const result = originalSetParent(node, preserveScalingSign, updatePivot);
            if (sceneObject.transform.parent === prevParent) return result;

            // A hop that puts the object back where the block found it returns
            // it to a state the policy already permitted, so it is always
            // allowed. Consulting the policy here is not merely redundant, it
            // is harmful: the policy resolves a gizmo's utility-layer anchor to
            // "no parent" and waves the outbound hops through as moves to the
            // root, which leaves the RETURN as the only hop it can refuse — and
            // refusing that strands the object at the top level. That is how a
            // bare selection could detach content from an imported subtree, or
            // silently lift it out of a flat CMS scene.
            const returning = !firstHop && sceneObject.transform.parent === sceneObject._reparentSettleFrom;
            if (!returning && !sceneObject._consultReparentPolicy()) {
                // Revert. Using the original (untrapped) setParent avoids
                // re-entering this interceptor and double-notifying.
                originalSetParent(prevParent, preserveScalingSign, updatePivot);
                // The revert is a return too, so Babylon appended it last again.
                if (left) restoreChildSlot(sceneObject.transform, left.parent, left.order);
                return result;
            }

            sceneObject._openReparentSettle(prevParent);
            sceneObject._trackChildSlot(left);

            // Dedup wherever the object lands somewhere this scene owns — which
            // is every hop a CALLER makes, and no hop of a borrow. Mid-borrow it
            // hangs off a node from the gizmo's own scene, or is parked at the
            // bare Babylon root; `getSiblings()` answers the latter with every
            // top-level object, strangers it is not a sibling of, and a rename
            // against them stuck, because the return hop never undoes one.
            //
            // Gating on the DESTINATION rather than on "is this the first hop"
            // matters: a borrow can already be in flight when a real drop
            // arrives in the same task, and that drop still owes its caller a
            // deduped name on the very next line.
            if (sceneObject._destinationIsOwnedByScene()) {
                sceneObject._resolveNameContention();
                sceneObject._carryAuthoredPose();
            }
            return result;
        };
    }

    /**
     * Keep the authored pose where it is in the world when a hop lands the
     * object under a parent of this scene, or a move comes to rest at the
     * bare root, as Babylon keeps the node: each behavior re-expresses the
     * local pose it holds, and in edit mode the new local is written into
     * authored data. Play leaves authored data alone, as a runtime move
     * always has. A hop back into the frame the pose is already in, such as
     * a borrow returning, changes nothing.
     */
    private _carryAuthoredPose(): void {
        const from = this._authoredParent;
        const parent = this.transform.parent;
        if (from === undefined || from === parent) return;
        const world = (node: Node | null) => (node ? node.computeWorldMatrix(true) : Matrix.Identity());
        for (const behavior of this.behaviors) behavior.onParentFrameChanged(world(from), world(parent));
        this._authoredParent = parent;
        if (!this._traceScene?.isPlayMode()) this.captureTransform();
    }

    /** True when `node` belongs to this scene's graph: a SceneObject's transform or animation node. */
    private _isSceneNode(node: Node): boolean {
        if (node.getScene?.() !== this.babylonScene) return false;
        return !!this._traceScene?.findSceneObjectByTransform(node);
    }

    /** True when the object's current parent is one this scene's graph owns. */
    private _destinationIsOwnedByScene(): boolean {
        const parent = this.transform.parent;
        // A bare Babylon root is not a destination this scene hands out — every
        // top-level object lives under the scene root SceneObject — so a node
        // parked at null is mid-borrow. `BoundingBoxGizmo.updateBoundingBox()`
        // opens with exactly that hop, and a scene whose own root is named
        // "Scene" would otherwise rename a user object of that name on nothing
        // more than a gizmo refresh. A move that genuinely comes to rest there
        // still dedups at settle.
        if (!parent) return false;
        return this._isSceneNode(parent);
    }

    /** Where this object sat when the current burst of hops began. */
    private _reparentSettleFrom: Node | null = null;
    private _reparentSettlePending = false;
    /**
     * The parent the authored pose is expressed under while a burst is open.
     * It only ever moves to a parent of this scene, or to the bare root where
     * a move rests, never to a borrower, so a borrow carries nothing.
     * Undefined when the burst began under a node this scene doesn't own (a
     * borrower's pivot): there is no frame to carry from.
     */
    private _authoredParent: Node | null | undefined = undefined;

    /**
     * Open the settle window for a parent change, keeping the FIRST hop's
     * parent — wherever the object is passed through next, that is where it
     * genuinely came from.
     */
    private _openReparentSettle(prevParent: Node | null): void {
        if (this._reparentSettlePending) return;
        this._reparentSettlePending = true;
        this._reparentSettleFrom = prevParent;
        this._authoredParent = !prevParent || this._isSceneNode(prevParent) ? prevParent : undefined;
        queueMicrotask(() => {
            if (this._reparentSettlePending) this._settleReparent();
        });
    }

    /**
     * Report the reparent, once, if the object ended up somewhere new.
     *
     * A node that comes to rest under the parent it started with was borrowed,
     * not moved, and reporting it is what let a gizmo attach feed itself: the
     * report re-solves layouts and re-syncs the editor, the re-sync re-attaches
     * the gizmo, and the gizmo borrows the node again. Nothing terminated it.
     *
     * Deferring is what makes the question answerable at all — mid-block there
     * is no way to know whether a hop ends the move — and it costs subscribers
     * nothing they were promised: this signal is documented as coarse and
     * bursty, with subscribers told to coalesce and re-scan (see
     * {@link TraceScene.onTopologyChanged}). One settled report per move is
     * strictly less noise than one per hop.
     */
    private _settleReparent(): void {
        this._reparentSettlePending = false;
        const from = this._reparentSettleFrom;
        this._reparentSettleFrom = null;
        this._childSlotsLeft = null;
        // No hop carried the authored pose to the bare root: only the settle
        // knows a move came to rest there.
        if (!this.transform.parent && !this.transform.isDisposed()) this._carryAuthoredPose();
        this._authoredParent = undefined;
        // Deleted inside the window: the deletion owns the notification.
        if (this.transform.isDisposed()) return;
        // Borrowed, not moved.
        if (this.transform.parent === from) return;

        // A move that came to rest somewhere no synchronous hop deduped against
        // (the bare root) still owes the scene a unique name. Idempotent for
        // the common case, which deduped on the caller's hop.
        this._resolveNameContention();
        this._notifyHierarchyChanged();
        // Post dedup + notification, so topology subscribers (the
        // reference-identity registry) see final names and hierarchy.
        this._traceScene?._emitTopologyChanged?.(this._changedAt(from));
        this._traceScene?._emitObjectReparented?.(this, from);
    }

    /**
     * Parents this object left in the current burst, each with its child order
     * as the hop out found it (#783). Cleared at settle, so coming back in a
     * later task lands last, like any move.
     */
    private _childSlotsLeft: Map<Node, Node[]> | null = null;

    /**
     * Remember the slot this hop left. If the hop brought the object back under
     * a parent it left earlier in the burst, put it back in that slot.
     */
    private _trackChildSlot(left: { parent: Node; order: Node[] } | null): void {
        const parent = this.transform.parent;
        const order = parent ? this._childSlotsLeft?.get(parent) : undefined;
        if (left) (this._childSlotsLeft ??= new Map()).set(left.parent, left.order);
        if (!parent || !order) return;
        this._childSlotsLeft?.delete(parent);
        restoreChildSlot(this.transform, parent, order);
    }

    /**
     * Ask the active TraceScene reparent policy whether the current parent
     * change is allowed. No scene or no policy → always allowed.
     */
    private _consultReparentPolicy(): boolean {
        if (!this._traceScene) return true;
        const parentTransform = this.transform.parent;
        const newParent = parentTransform
            ? this._traceScene.findSceneObjectByTransform(parentTransform) ?? null
            : null;
        return this._traceScene._isReparentAllowed(this, newParent);
    }

    /**
     * Check for sibling name conflicts at the current parent and auto-rename
     * if needed. Called after external reparenting (e.g. Inspector drag-drop).
     */
    private _resolveNameContention(): void {
        // Build sibling names excluding self (by identity, not by name — another
        // sibling may already have the same name, which is exactly the conflict).
        const siblings = this.getSiblings();
        const siblingNames = new Set<string>();
        for (const sib of siblings) {
            if (sib !== this) siblingNames.add(sib.name);
        }
        if (!siblingNames.has(this.name)) return;

        const oldName = this.name;
        const uniqueName = Utility.generateUniqueName(oldName, siblingNames);
        this._setName(uniqueName);
        Logger.log(`[SceneObject] Renamed "${oldName}" → "${uniqueName}" to avoid sibling conflict`);
        this._traceScene._notifyObjectRenamed(this, oldName, uniqueName);
    }

    /**
     * Subscribe to the transform's enabled-state observable and seed the
     * cached effective state. Called once from both constructor paths, right
     * after the transform is assigned.
     */
    private _installEffectiveActiveTracking(): void {
        this._effectiveActive = this.transform.isEnabled();
        this._enabledStateObserver = this.transform.onEnabledStateChangedObservable.add(() => {
            this._refreshEffectiveActive();
        });
    }

    /**
     * Recompute effective active for this object and its SceneObject
     * descendants, dispatching `onEffectiveActiveChanged` to behaviors on
     * actual flips. Descent prunes at objects whose OWN enabled flag is
     * false — their effective state is pinned false regardless of ancestors,
     * so (for example) a hidden inner deck's pages never re-fire when an
     * outer page toggles.
     *
     * Triggered by the enabled observable (any setEnabled on this transform)
     * and by hierarchy changes (reparenting can flip effective state with no
     * setEnabled call).
     * @internal
     */
    _refreshEffectiveActive(): void {
        const effective = this.transform.isEnabled();
        // No flip → nothing below can have flipped either: a descendant's
        // effective state only changes THROUGH this node on this path (its
        // own toggles fire its own observable). Pruning here keeps the
        // per-setEnabled cost proportional to what actually changed —
        // reparent sweeps and redundant toggles cost O(1).
        if (effective === this._effectiveActive) return;
        this._effectiveActive = effective;
        for (const behavior of this.behaviors) {
            behavior._onEffectiveActiveChanged(effective);
        }
        for (const child of this.getChildren()) {
            if (child.transform.isEnabled(false)) {
                child._refreshEffectiveActive();
            }
        }
    }

    /**
     * Notify this object and all descendants that the ancestor chain changed
     * (e.g. after reparenting). Both SceneObject-level $refs (active/src/transform)
     * and behavior-level $refs are re-subscribed and re-resolved.
     */
    private _notifyHierarchyChanged(): void {
        // Reparenting can flip effective activation without any setEnabled
        // call (moved into/out of a disabled subtree).
        this._refreshEffectiveActive();
        // SceneObject-level $refs on this object
        this._resubscribeAndResolve();
        for (const behavior of this.behaviors) {
            behavior._resubscribeAndResolve();
        }
        for (const desc of this.getDescendants()) {
            desc._resubscribeAndResolve();
            for (const behavior of desc.getBehaviors()) {
                behavior._resubscribeAndResolve();
            }
        }
    }

    /**
     * Get or create the animation root for this SceneObject.
     * The animation root is an intermediate TransformNode between
     * this.transform and visual content. Animations target this node
     * to keep the SceneObject's logical transform stable.
     *
     * First call creates the node and reparents existing children.
     * Subsequent calls return the existing node (idempotent).
     */
    getOrCreateAnimationRoot(): TransformNode {
        if (this._animationRoot) return this._animationRoot;

        this._animationRoot = new TransformNode(
            `${this.name}__animRoot`,
            this.babylonScene
        );
        this._animationRoot.parent = this.transform;

        // Reparent existing children to animation root
        const children = this.transform.getChildren();
        for (const child of children) {
            if (child !== this._animationRoot) {
                child.parent = this._animationRoot;
            }
        }

        // Notify behaviors so they can reparent non-Node content (e.g. TextRenderer)
        for (const behavior of this.behaviors) {
            behavior.onAnimationRootCreated(this._animationRoot);
        }

        Logger.log(`[SceneObject] Created animation root for ${this.name}`);
        return this._animationRoot;
    }

    /** Get the animation root if it exists (does not create one). */
    get animationRoot(): TransformNode | null {
        return this._animationRoot;
    }

    /** Check whether a given node is this SceneObject's animation root. */
    isAnimationRoot(node: Node): boolean {
        return node === this._animationRoot;
    }

    /**
     * Get or create the animation layer for this SceneObject.
     * The animation layer is a second runtime TransformNode inserted BETWEEN
     * this.transform and the animation root, so two clips can compose:
     * sequence transitions (enter/exit) animate the layer while a
     * while-active loop animates the root underneath — nested transforms
     * give correct per-channel composition (positions add through the
     * hierarchy, rotations and scales multiply), so phase handoffs cannot
     * jump. Content and children keep living under the animation root;
     * only what each clip animates changes.
     *
     * Created lazily (only objects that need layered playback get one),
     * NOT serialized, and skipped transparently by traversal like the
     * animation root. First call reparents the animation root under it.
     */
    getOrCreateAnimationLayer(): TransformNode {
        if (this._animationLayer) return this._animationLayer;

        const root = this.getOrCreateAnimationRoot();
        this._animationLayer = new TransformNode(
            `${this.name}__animLayer`,
            this.babylonScene
        );
        this._animationLayer.parent = this.transform;
        root.parent = this._animationLayer;

        Logger.debug(`[SceneObject] Created animation layer for ${this.name}`);
        return this._animationLayer;
    }

    /** Get the animation layer if it exists (does not create one). */
    get animationLayer(): TransformNode | null {
        return this._animationLayer;
    }

    /** Check whether a given node is this SceneObject's animation layer. */
    isAnimationLayer(node: Node): boolean {
        return node === this._animationLayer;
    }

    /**
     * Flatten the animation root back into the transform hierarchy.
     * Reparents animation root's children to this.transform, then disposes it.
     * Used during destroy/reload to clean up the runtime node.
     */
    private _flattenAnimationRoot(): void {
        if (!this._animationRoot) return;

        const animRootChildren = this._animationRoot.getChildren();
        for (const child of animRootChildren) {
            child.parent = this.transform;
        }
        this._animationRoot.dispose();
        this._animationRoot = null;
        if (this._animationLayer) {
            this._animationLayer.dispose();
            this._animationLayer = null;
        }
    }

    /**
     * Get child TransformNodes, transparently skipping the animation root.
     * Returns raw Babylon TransformNodes — use `getChildren()` for SceneObject-level results.
     */
    getChildTransforms(): TransformNode[] {
        if (this._animationRoot) {
            const directChildren = this.transform.getChildren<TransformNode>().filter(
                child => child !== this._animationRoot && child !== this._animationLayer
            );
            const animChildren = this._animationRoot.getChildren<TransformNode>();
            return [...directChildren, ...animChildren];
        }
        return this.transform.getChildren<TransformNode>();
    }

    /**
     * Get direct child SceneObjects.
     * Filters out non-SceneObject transforms (meshes, internal nodes).
     */
    getChildren(): SceneObject[] {
        // Child resolution rides the scene registry; hosts without one
        // (detached objects, partial test scenes) have no resolvable
        // children. One choke point — callers (including observable-driven
        // walks like _refreshEffectiveActive) need no guards of their own.
        if (typeof this._traceScene?.findSceneObjectByTransform !== 'function') return [];
        const childTransforms = this.getChildTransforms();
        const seen = new Set<SceneObject>();
        const result: SceneObject[] = [];
        for (const t of childTransforms) {
            const obj = this._traceScene.findSceneObjectByTransform(t);
            if (obj && !seen.has(obj)) {
                seen.add(obj);
                result.push(obj);
            }
        }
        return result;
    }

    /**
     * Meshes THIS object owns: descendants of its transform, pruned at every
     * child SceneObject's root. Child objects parent under this transform, so
     * an unpruned walk (getObjectMeshes bare) would enumerate their meshes as
     * if they were this object's — and anything keying slots off that list
     * (Renderer pinning, MaterialPreviews slot detection) would let a parent's
     * materials/opacity/tint leak onto child objects. Index order is the same
     * pre-order walk as getObjectMeshes, minus the foreign subtrees.
     *
     * Hosts without a scene registry (detached objects, partial test scenes)
     * resolve no children, so this degrades to the unpruned enumeration.
     */
    getOwnedMeshes(): AbstractMesh[] {
        const children = this.getChildren();
        if (children.length === 0) return getObjectMeshes(this.transform);
        return getObjectMeshes(this.transform, new Set<Node>(children.map((c) => c.transform)));
    }

    /**
     * Get all descendant SceneObjects (excludes self).
     */
    getDescendants(): SceneObject[] {
        const descendantTransforms = this.transform.getDescendants(false);
        const seen = new Set<SceneObject>([this]);
        const result: SceneObject[] = [];
        for (const t of descendantTransforms) {
            const obj = this._traceScene.findSceneObjectByTransform(t);
            if (obj && !seen.has(obj)) {
                seen.add(obj);
                result.push(obj);
            }
        }
        return result;
    }

    /**
     * The nearest self-or-ancestor that is a **placement**: a scene reference
     * whose `src` resolved to a DOCUMENT, so the content below it belongs to
     * that document and not to this scene (SCENE_ROOT_SPEC §2.3).
     *
     * This is the rule an editor needs to implement "copy on inside-edit": a
     * scene never edits the inside of a reference, so an edit landing on a node
     * whose `referenceAncestor()` is not the node itself has to unpack
     * ({@link TraceScene.unpackReference}) first — the asset's nodes become the
     * scene's own and the edit lands on them. Edits to the placement itself —
     * its transform, a behavior on it, a parameter value — never copy, which is
     * why the placement answers with ITSELF and a host compares identities
     * rather than testing for null.
     *
     * A reference that resolved to a FILE is row 1 of the rule table: it owns
     * the file, has no content of another document below it, and is therefore
     * not a placement in this sense — styling it is an ordinary edit. That is
     * what "content was loaded beneath it" distinguishes, and why this is not
     * simply `isSceneReference`.
     *
     * @returns the placement, or `null` when nothing in the chain is one.
     */
    referenceAncestor(): SceneObject | null {
        for (let node: SceneObject | null = this; node; node = node.getParent()) {
            if (node.isSceneReference && node.getChildren().length > 0) return node;
        }
        return null;
    }

    /**
     * Get parent SceneObject, or null if at root level.
     */
    getParent(): SceneObject | null {
        const parentTransform = this.transform.parent;
        if (!parentTransform) return null;
        return this._traceScene.findSceneObjectByTransform(parentTransform) ?? null;
    }

    /**
     * Get sibling SceneObjects (including self).
     */
    getSiblings(): SceneObject[] {
        const parent = this.getParent();
        if (!parent) {
            return this._traceScene.getSceneObjects().filter(obj => !obj.transform.parent);
        }
        return parent.getChildren();
    }

    get position(): Vector3 {
        return this.transform.position;
    }

    set position(pos: Vector3) {
        this.transform.position = pos;
    }

    get rotation(): Vector3 {
        return this.transform.rotation;
    }

    set rotation(rot: Vector3) {
        this.transform.rotation = rot;
    }

    get scale(): Vector3 {
        return this.transform.scaling;
    }

    set scale(scale: Vector3) {
        this.transform.scaling = scale;
    }

    /**
     * Helper function to recursively remove null and undefined values from an object
     * @param obj - Object to clean
     * @returns Cleaned object with null/undefined values removed
     */
    private removeNullValues(obj: unknown): unknown {
        if (obj === null || obj === undefined) {
            return undefined;
        }

        if (Array.isArray(obj)) {
            return obj
                .map(item => this.removeNullValues(item))
                .filter(item => item !== undefined && item !== null);
        }

        if (typeof obj === 'object') {
            const cleaned: Record<string, unknown> = {};
            for (const key in obj as Record<string, unknown>) {
                if (Object.hasOwn(obj, key)) {
                    const value = this.removeNullValues((obj as Record<string, unknown>)[key]);
                    if (value !== undefined && value !== null) {
                        cleaned[key] = value;
                    }
                }
            }
            return cleaned;
        }

        return obj;
    }

    /**
     * Transform fields a runtime system currently writes on this object —
     * runtime-only bookkeeping, never serialized. Owner-keyed by identity:
     * each writer registers its own claim and a release retracts only that
     * writer's entry, so an owner releasing a departed object (possibly
     * after another owner adopted it) can never clobber the new owner's
     * marks. Consolidating capture/serialization precedence onto this
     * registry is the follow-up in #687.
     */
    private _runtimeTransformFieldOwners = new Map<object, { position: boolean; scale: boolean }>();

    /**
     * @internal Runtime-writer bookkeeping. Records which transform fields
     * `owner` currently writes on this object. Both-false releases the claim.
     * Note {@link captureTransform} does NOT read this today — capture
     * precedence is baseline-first regardless of ownership (see its doc);
     * the registry is the writer's own record and the seam #687 will fold
     * that precedence onto.
     */
    setRuntimeTransformFieldOwner(owner: object, fields: { position: boolean; scale: boolean }): void {
        if (!fields.position && !fields.scale) {
            this._runtimeTransformFieldOwners.delete(owner);
        } else {
            this._runtimeTransformFieldOwners.set(owner, { ...fields });
        }
    }

    /** Which transform fields some runtime writer currently owns (union of claims). */
    getRuntimeOwnedTransformFields(): { position: boolean; scale: boolean } {
        let position = false;
        let scale = false;
        for (const fields of this._runtimeTransformFieldOwners.values()) {
            position ||= fields.position;
            scale ||= fields.scale;
        }
        return { position, scale };
    }

    /**
     * Capture runtime transform state into sceneData for serialization.
     * Copies the Babylon.js transform node's position/rotation/scale into sceneData.transform.
     *
     * Serialization precedence, EXPLICIT (#662, review of #679; consolidation
     * follow-up in #687):
     *
     * 1. A Properties saved baseline exists (a state/contribution override is
     *    active, or the editor primed one) → capture the BASELINE, all
     *    fields. The live pose may be an absolute state pose written via
     *    `setTransform` — serializing it would bake the state into authored
     *    Base, corrupting revert-to-Base and exports. This holds even for
     *    fields a runtime writer (Layout) currently owns: Layout writes each
     *    field it claims through to a held baseline and, outside Play, to
     *    authored sceneData (its rest pose), so preferring the baseline
     *    preserves the export-conformance contract
     *    (docs/layout/UNITY_SUPPORT.md §1) instead of un-baking it.
     *
     * 2. No baseline → capture LIVE. This is where a layout arrangement (or
     *    any other runtime write) bakes into ordinary TRS.
     *
     * Note the two rules above are the WHOLE policy: field ownership does not
     * enter into it. {@link setRuntimeTransformFieldOwner} records which
     * fields a runtime system is writing, but capture deliberately does not
     * consult it — an earlier revision let ownership win over the baseline
     * and that is what baked active state poses into Base (#662). The
     * writer-side rationale lives with the writer (see Layout's ownership
     * marks in src/behaviors/layout.ts).
     */
    captureTransform() {
        // Only capture src if it's not a $name reference
        if (!this._isPropertyRef(this._sceneObjectData.src)) {
            this._sceneObjectData.src = this._src;
        }
        if (!this.transform) return;

        if (!this._sceneObjectData.transform) {
            this._sceneObjectData.transform = {};
        }
        const t = this._sceneObjectData.transform;

        const props = this.getBehavior('Properties');
        const authored = props?.getAuthoredTransform();

        if (authored) {
            t.translation = [authored.position.x, authored.position.y, authored.position.z];
            t.scale = [authored.scale.x, authored.scale.y, authored.scale.z];
            t.rotation = [authored.rotation.x, authored.rotation.y, authored.rotation.z, authored.rotation.w];
            return;
        }

        t.translation = [this.transform.position.x, this.transform.position.y, this.transform.position.z];
        t.scale = [this.transform.scaling.x, this.transform.scaling.y, this.transform.scaling.z];

        // Capture rotation - prefer quaternion if set, otherwise convert from Euler
        let quat: Quaternion;
        if (this.transform.rotationQuaternion) {
            quat = this.transform.rotationQuaternion;
        } else {
            quat = Quaternion.FromEulerAngles(
                this.transform.rotation.x,
                this.transform.rotation.y,
                this.transform.rotation.z
            );
        }
        t.rotation = [quat.x, quat.y, quat.z, quat.w];
    }

    /** Check if a value is a property reference string (`$name` or `{name}`) */
    private _isPropertyRef(value: unknown): boolean {
        return typeof value === 'string' && WHOLE_REF_RE.test(value);
    }

    /**
     * Serialize the SceneObject's current runtime state into sceneData for export.
     * Captures transform and serializes all behaviors into sceneData.components,
     * except while a rebuild of them is loading (the authored record stays).
     * @param skipTransformCapture - If true, skips capturing the current transform state.
     *                               Useful when an animation is playing to avoid freezing the object.
     */
    refreshSceneData(skipTransformCapture: boolean = false) {
        if (!skipTransformCapture) {
            this.captureTransform();
        }

        // A rebuild from this very record is still downloading: destroy() emptied
        // the runtime list, but nothing authored is gone. Writing the empty list
        // over the record would lose it, so a save in this window keeps what was authored.
        if (this.isLoadingBehaviors && this.behaviors.length === 0) return;

        // Serialize components directly from behaviors (behaviors own their data)
        const components = this.serializeBehaviors();

        // Clean components - removes null/undefined values but preserves @type and other valid properties
        this._sceneObjectData.behaviors = (components
            .map(component => this.removeNullValues(component))
            .filter(component => component && typeof component === 'object' && '@type' in component)) as BehaviorData[];

        Logger.log(`components now is ${JSON.stringify(this._sceneObjectData.behaviors)}`);
    }

    /**
     * Replace SceneObject data and reload the object.
     * This is the primary API for updating an object's configuration.
     *
     * The data is cloned to establish clear ownership - callers should not
     * mutate the object after passing it to this method.
     *
     * @param data - New scene data to apply (will be cloned)
     * @returns The behaviors array after reload
     *
     * @example
     * ```typescript
     * const data = sceneObject.getSceneObjectData();
     * data.behaviors.push({ '@type': 'Frame', color: '#FF0000' });
     * const behaviors = await sceneObject.setData(data);
     * ```
     */
    async setData(data: SceneObjectData): Promise<BehaviorData[]> {
        // Unsubscribe from old property providers before replacing data
        this._unsubscribeFromProperties();

        // Clone incoming data to prevent issues with frozen/Immer objects
        this._sceneObjectData = structuredClone(data);

        // Re-scan and resolve $name refs in the new data
        this._scanAndResolveProperties();

        await this.reload();

        // Re-subscribe to properties after reload (destroy() cleared subscriptions)
        this._subscribeToProperties();

        // Serialize behaviors after refresh to get the latest state
        this.refreshSceneData();

        return this._sceneObjectData.behaviors || [];
    }

    /**
     * Swap this object's asset source and reload in place — the design-set
     * form-swap primitive: the same node points at a different component
     * asset and re-materializes from it.
     *
     * Identity-preservation contract (inherited from {@link reload}, same as
     * {@link setData}): the SceneObject instance survives; child SceneObjects
     * are reparented onto the recreated transform with sibling order
     * restored; behaviors are re-materialized as FRESH instances from the
     * authored data in sceneData — every previously fetched behavior
     * reference is stale once this resolves.
     *
     * Layout hand-off: a swap does not lose layout management. On its next
     * pass the owning Layout re-feeds LayoutSizeReceivers (and sweeps the
     * detached old receiver), so a swapped smart component re-realizes the
     * SAME resolved box its slot already granted — the new form arrives
     * pre-sized.
     *
     * Unlike the inspector's debug `source` setter, this stamps
     * `sceneData.src` too, so the swap survives serialization.
     */
    async setSrc(src: string): Promise<void> {
        // Compare the AUTHORED value, like the property-driven src paths in
        // _onPropertyChanged/_resubscribeAndResolve compare theirs: src can
        // be a `$ref`, and a same-ref call must not tear down and reload the
        // whole object for nothing.
        if (this._sceneObjectData.src === src) return;
        this._sceneObjectData.src = src;
        await this.setData(this._sceneObjectData);
    }

    destroy(excludeChildren: TransformNode[] = []): void {
        // Cancel any running transform transitions
        this.cancelTransformTransition();

        // Stop effective-active tracking before teardown flips enabled states
        if (this._enabledStateObserver) {
            this.transform.onEnabledStateChangedObservable.remove(this._enabledStateObserver);
            this._enabledStateObserver = null;
        }

        // Unsubscribe from property providers
        this._unsubscribeFromProperties();

        // Flatten animation root so its children become direct children
        this._flattenAnimationRoot();

        const shouldExclude = new Set(excludeChildren);

        // Get all children from Babylon's scene graph
        const allChildren = this.transform.getChildren<TransformNode>();

        // Unparent excluded children first so they survive transform disposal
        for (const child of allChildren) {
            if (shouldExclude.has(child)) {
                child.parent = null;
            }
        }

        // Dispose children that are not excluded
        for (const child of allChildren) {
            if (!shouldExclude.has(child)) {
                // recurse into children's children
                child.dispose();
            }
        }

        // Detach and dispose all behaviors before clearing
        for (const behavior of this.behaviors) {
            // Remove from Babylon's behavior system
            this.transform.removeBehavior(behavior);
            // Call detach (TraceBehavior always has detach)
            behavior.detach();
        }

        this._animationClips = new Map();
        this._setBoundAnimationClips(new Map());

        this.transform.dispose(false);

        this.behaviors = [];
        this._traceScene?._bumpStructureRevision?.();
    }

    // ── Property reference support ($name in active/src/transform) ──
    // Implements PropertySubscriber so SceneObject can subscribe to Properties
    // alongside behaviors for reactive updates.

    /**
     * Scan authored SceneObjectData for `$name` references and resolve them.
     * Called at construction and after setData().
     */
    private _scanAndResolveProperties(): void {
        this._propertyNames = this._scanSceneObjectPropertyNames();
        this._resolveSceneObjectFields();
    }

    /**
     * Scan SceneObjectData fields (not behaviors) for property references.
     * Supports `$name` and `{name}` syntax. Checks `active` and `src`.
     */
    private _scanSceneObjectPropertyNames(): Set<string> {
        const names = new Set<string>();

        // Check active
        if (typeof this._sceneObjectData.active === 'string') {
            const m = this._sceneObjectData.active.match(WHOLE_REF_RE);
            if (m) names.add(wholeRefName(m));
        }

        // Check src
        if (typeof this._sceneObjectData.src === 'string') {
            const m = this._sceneObjectData.src.match(WHOLE_REF_RE);
            if (m) names.add(wholeRefName(m));
        }

        return names;
    }

    /**
     * Resolve SceneObject-level property refs into runtime fields.
     * Supports `$name` and `{name}` syntax.
     * Updates `_resolvedActive`, `_src`, and Babylon transform values.
     */
    private _resolveSceneObjectFields(): void {
        // Resolve active
        const authoredActive = this._sceneObjectData.active;
        if (typeof authoredActive === 'string') {
            const m = authoredActive.match(WHOLE_REF_RE);
            const resolved = m ? this.resolveProperty(wholeRefName(m)) : undefined;
            this._resolvedActive = typeof resolved === 'boolean' ? resolved : true;
        } else {
            this._resolvedActive = authoredActive !== false;
        }

        // Resolve src
        if (this._sceneObjectData.src) {
            if (this._isPropertyRef(this._sceneObjectData.src)) {
                const m = this._sceneObjectData.src.match(WHOLE_REF_RE);
                const name = m ? wholeRefName(m) : '';
                const resolved = this.resolveProperty(name);
                this._src = typeof resolved === 'string' ? resolved : '';
            } else {
                this._src = this._sceneObjectData.src;
            }
        } else {
            this._src = '';
        }
    }

    /**
     * Subscribe to ancestor Properties for each name in `_propertyNames`.
     * Mirrors TraceBehavior's subscription pattern.
     */
    private _subscribeToProperties(): void {
        this._unsubscribeFromProperties();
        if (this._propertyNames.size === 0) return;

        for (const name of this._propertyNames) {
            let current: SceneObject | null = this;
            while (current) {
                const props = current.getBehavior('Properties');
                if (props?.hasProperty(name)) {
                    props.subscribe(name, this);
                    this._subscribedProperties.set(name, props);
                    break;
                }
                current = current.getParent();
            }
        }
    }

    /** Unsubscribe from all property providers */
    private _unsubscribeFromProperties(): void {
        for (const [name, props] of this._subscribedProperties) {
            props.unsubscribe(name, this);
        }
        this._subscribedProperties.clear();
    }

    /**
     * Called by a PropertyProvider when a subscribed property value changes.
     * Re-resolves affected fields and applies changes to the Babylon runtime.
     * @implements PropertySubscriber
     */
    _onPropertyChanged(_name: string): void {
        const prevActive = this.active;
        const prevSrc = this._src;

        this._resolveSceneObjectFields();

        // Apply active change
        if (this.active !== prevActive) {
            this.transform.setEnabled(this.active);
        }

        // Apply src change — triggers full reload
        if (this._src !== prevSrc) {
            this.reload().catch(e =>
                Logger.warn(`[SceneObject] reload failed after property change:`, e)
            );
        }
    }

    /**
     * Re-subscribe to properties and re-resolve.
     * Called when the ancestor chain changes (reparenting) or when a
     * Properties behavior is added/removed from an ancestor.
     * @implements PropertySubscriber
     */
    _resubscribeAndResolve(): void {
        if (this._propertyNames.size === 0) return;
        this._subscribeToProperties();

        const prevActive = this.active;
        const prevSrc = this._src;

        this._resolveSceneObjectFields();

        if (this.active !== prevActive) {
            this.transform.setEnabled(this.active);
        }
        if (this._src !== prevSrc) {
            this.reload().catch(e =>
                Logger.warn(`[SceneObject] reload failed after resubscribe:`, e)
            );
        }
    }


    // ── Transform transitions ──
    //
    // One TransitionManager channel per transform component, keyed
    // `transform:{pos|rot|scale}:{uniqueId}` — mirroring KHR_interactivity's
    // pointer/interpolate semantics where each transform pointer
    // (/nodes/N/translation, .../rotation, .../scale) interpolates
    // independently:
    //   - a new interpolation on a pointer REPLACES the one running on that
    //     pointer (its pending completion is dropped);
    //   - a snap write (pointer/set) on a pointer CANCELS only that pointer's
    //     interpolation;
    //   - other pointers' in-flight interpolations are never disturbed.

    /**
     * @internal Start (or snap) the position channel.
     * `onComplete` fires only when an eased transition runs to natural
     * completion — never on replacement or cancellation, and not on the
     * synchronous snap path (KHR pointer/interpolate `done` semantics).
     * Used by PointerInterpolateBlock; public callers use setPosition().
     */
    _startPositionChannel(value: Vector3, transition?: TransitionConfig | null, onComplete?: () => void): void {
        if (!transition || !isTimeTransition(transition) || transition.duration <= 0) {
            // Snap immediately (also snaps for property-driven transitions —
            // those are handled at the Properties level, not here)
            this._cancelTransformChannel('pos');
            this.transform.position.copyFrom(value);
            return;
        }
        const from = this.transform.position.clone();
        const to = value.clone();
        this._runTransformChannel('pos', transition, (progress) => {
            // Strict equality: completion delivers exactly 1; expressive
            // easings exceed 1 MID-FLIGHT (overshoot) and must interpolate
            if (progress === 1) {
                this.transform.position.copyFrom(to);
            } else {
                Vector3.LerpToRef(from, to, progress, this.transform.position);
            }
        }, onComplete);
    }

    /** @internal Start (or snap) the rotation channel. See _startPositionChannel. */
    _startRotationChannel(value: Quaternion, transition?: TransitionConfig | null, onComplete?: () => void): void {
        if (!transition || !isTimeTransition(transition) || transition.duration <= 0) {
            this._cancelTransformChannel('rot');
            if (this.transform.rotationQuaternion) {
                this.transform.rotationQuaternion.copyFrom(value);
            } else {
                this.transform.rotationQuaternion = value.clone();
            }
            return;
        }
        const from = this.transform.rotationQuaternion?.clone() ?? Quaternion.Identity();
        const to = value.clone();
        this._runTransformChannel('rot', transition, (progress) => {
            if (!this.transform.rotationQuaternion) {
                this.transform.rotationQuaternion = Quaternion.Identity();
            }
            if (progress === 1) {
                this.transform.rotationQuaternion.copyFrom(to);
            } else {
                Quaternion.SlerpToRef(from, to, progress, this.transform.rotationQuaternion);
            }
        }, onComplete);
    }

    /** @internal Start (or snap) the scale channel. See _startPositionChannel. */
    _startScaleChannel(value: Vector3, transition?: TransitionConfig | null, onComplete?: () => void): void {
        if (!transition || !isTimeTransition(transition) || transition.duration <= 0) {
            this._cancelTransformChannel('scale');
            this.transform.scaling.copyFrom(value);
            return;
        }
        const from = this.transform.scaling.clone();
        const to = value.clone();
        this._runTransformChannel('scale', transition, (progress) => {
            if (progress === 1) {
                this.transform.scaling.copyFrom(to);
            } else {
                Vector3.LerpToRef(from, to, progress, this.transform.scaling);
            }
        }, onComplete);
    }

    private _transformChannelKey(channel: TransformChannel): string {
        return `transform:${channel}:${this.transform.uniqueId}`;
    }

    /**
     * Start (or replace) one component channel in TransitionManager. A start
     * on an already-running channel replaces it silently — the replaced
     * transition's onComplete is dropped (KHR: a new interpolation on the same
     * pointer cancels the previous one's pending completion).
     */
    private _runTransformChannel(
        channel: TransformChannel,
        transition: TransitionConfig,
        onUpdate: (progress: number) => void,
        onComplete?: () => void,
    ): void {
        if (!isTimeTransition(transition)) return; // callers pre-check; defensive
        this._activeTransformChannels.add(channel);
        getTransitionManager(this.babylonScene).start(
            this._transformChannelKey(channel),
            transition,
            onUpdate,
            () => {
                this._activeTransformChannels.delete(channel);
                onComplete?.();
            },
        );
    }

    /** Cancel one component channel without completing it. */
    private _cancelTransformChannel(channel: TransformChannel): void {
        if (!this._activeTransformChannels.has(channel)) return;
        getTransitionManager(this.babylonScene).cancel(this._transformChannelKey(channel));
        this._activeTransformChannels.delete(channel);
    }

    /** Cancel all active transform-transition channels (position, rotation, scale). */
    cancelTransformTransition(): void {
        this._cancelTransformChannel('pos');
        this._cancelTransformChannel('rot');
        this._cancelTransformChannel('scale');
    }

    /** Whether any transform-component transition is currently active. */
    get isTransformTransitioning(): boolean {
        return this._activeTransformChannels.size > 0;
    }

    private setupInspectableProperties(): void {
        // Babylon accesses getters/setters directly on transformnode
        Object.defineProperty(this.transform, "source", {
            configurable:true,
            get: () => this._src,
            set: (value: string) => { this._src = value; },
        });

        this.transform.inspectableCustomProperties = [
            {
                label: "SceneObj src",
                propertyName: "source",
                type: InspectableType.String,
            },
            {
                label: "Refresh SceneObject",
                propertyName: "refreshSrc",
                callback: () => {
                    // problem is that a sceneobject isn't a babylon object, so the properties are
                    // being attached to the transformnode object
                    Logger.log(`Refreshing: ${this._src}`);
                    void this.reload();
                },
                type: InspectableType.Button,
            },
        ];
    }
}
